import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { navigationLinks, companyInfo } from "../mockData";
import {
  Menu,
  X,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
} from "lucide-react";
import logo from "../assets/logo_meet_enterprise-removebg-preview.png";

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/98 backdrop-blur-xl shadow-lg"
          : "bg-white shadow-sm"
      }`}
    >
      {/* Desktop */}
      <div className="hidden md:block">
        {/* Top Contact Bar */}
        <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-pink-600 py-2.5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between text-white text-sm">
              <div className="flex items-center gap-8">
                <a
                  href={`tel:${companyInfo.phone1}`}
                  className="flex items-center gap-2 hover:text-white/90 transition-colors group"
                >
                  <div className="p-1.5 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors">
                    <Phone className="w-3.5 h-3.5" />
                  </div>
                  <span className="font-medium">{companyInfo.phone1}</span>
                </a>
                <a
                  href={`tel:${companyInfo.phone2}`}
                  className="flex items-center gap-2 hover:text-white/90 transition-colors group"
                >
                  <div className="p-1.5 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors">
                    <Phone className="w-3.5 h-3.5" />
                  </div>
                  <span className="font-medium">{companyInfo.phone2}</span>
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-2 hover:text-white/90 transition-colors group"
                >
                  <div className="p-1.5 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors">
                    <Mail className="w-3.5 h-3.5" />
                  </div>
                  <span className="font-medium">{companyInfo.email}</span>
                </a>
              </div>
              <a
                href="https://g.co/kgs/JBGXRww"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-white/90 transition-colors group"
              >
                <div className="p-1.5 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors">
                  <MapPin className="w-3.5 h-3.5" />
                </div>
                <span className="font-medium">Ahmedabad, Gujarat, India</span>
              </a>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="flex items-center shrink-0 group">
              <img
                src={logo}
                alt="Meet Enterprise"
                className={`w-auto object-contain transition-all duration-300 ${
                  scrolled ? "h-16" : "h-20"
                }`}
              />
            </Link>

            <nav className="flex items-center gap-1">
              {navigationLinks.map((link) =>
                link.dropdown ? (
                  <div key={link.name} className="relative group">
                    <Link
                      to="/products"
                      className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:text-purple-700 rounded-lg hover:bg-purple-50 transition-all duration-200"
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                    </Link>

                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 overflow-hidden z-50 transform group-hover:translate-y-0">
                      <div className="py-2">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 hover:text-purple-700 transition-all"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                      location.pathname === link.path
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md"
                        : "text-gray-700 hover:text-purple-700 hover:bg-purple-50"
                    }`}
                  >
                    {link.name}
                  </Link>
                ),
              )}
              <Link
                to="/contact"
                className="ml-4 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                Get Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        {/* Mobile Contact Bar */}
        <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-pink-600 py-2.5 px-4">
          <div className="flex items-center justify-between text-white text-xs">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-1.5 hover:text-white/90 transition-colors group"
            >
              <div className="p-1 bg-white/20 rounded-md group-hover:bg-white/30 transition-colors">
                <Phone className="w-3 h-3" />
              </div>
              <span className="font-medium">{companyInfo.phone}</span>
            </a>
            <a
              href={`tel:${companyInfo.phone1}`}
              className="flex items-center gap-1.5 hover:text-white/90 transition-colors group"
            >
              <div className="p-1 bg-white/20 rounded-md group-hover:bg-white/30 transition-colors">
                <Phone className="w-3 h-3" />
              </div>
              <span className="font-medium">{companyInfo.phone2}</span>
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-1.5 hover:text-white/90 transition-colors group"
            >
              <div className="p-1 bg-white/20 rounded-md group-hover:bg-white/30 transition-colors">
                <Mail className="w-3 h-3" />
              </div>
              <span className="font-medium">{companyInfo.email}</span>
            </a>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="px-4 bg-white">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img
                src={logo}
                alt="Meet Enterprise"
                className="h-14 w-auto object-contain"
              />
            </Link>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-900" />
              ) : (
                <Menu className="w-6 h-6 text-gray-900" />
              )}
            </button>
          </div>
        </div>

        <div
          className={`overflow-hidden transition-all duration-300 ${
            mobileMenuOpen ? "max-h-[800px] pb-4" : "max-h-0"
          }`}
        >
          <div className="bg-gray-50 border-t border-gray-100">
            <div className="px-4 py-4 space-y-2">
              {navigationLinks.map((link) =>
                link.dropdown ? (
                  <div key={link.name}>
                    <button
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === link.name ? null : link.name,
                        )
                      }
                      className="w-full flex items-center justify-between py-3 px-3 text-sm font-medium rounded-lg transition-all text-gray-700 hover:text-purple-700 hover:bg-purple-50"
                    >
                      {link.name}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${openDropdown === link.name ? "rotate-180" : ""}`}
                      />
                    </button>

                    {openDropdown === link.name && (
                      <div className="pl-4 space-y-1">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setOpenDropdown(null);
                            }}
                            className="block py-2.5 px-3 text-sm text-gray-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-all"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block py-3 px-3 text-sm font-medium rounded-lg transition-all ${
                      location.pathname === link.path
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                        : "text-gray-700 hover:text-purple-700 hover:bg-purple-50"
                    }`}
                  >
                    {link.name}
                  </Link>
                ),
              )}

              {/* CTA Button */}
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block mt-4 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-xl text-center hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
              >
                Get Quote
              </Link>

              {/* Contact Details in Mobile Menu */}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Contact Us
                </div>
                <div className="space-y-3">
                  <a
                    href={`tel:${companyInfo.phone1}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 text-sm text-gray-700 hover:text-purple-700 p-2 hover:bg-purple-50 rounded-lg transition-all"
                  >
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Phone className="w-4 h-4 text-purple-600" />
                    </div>
                    <span>{companyInfo.phone1}</span>
                  </a>
                  <a
                    href={`tel:${companyInfo.phone2}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 text-sm text-gray-700 hover:text-purple-700 p-2 hover:bg-purple-50 rounded-lg transition-all"
                  >
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Phone className="w-4 h-4 text-purple-600" />
                    </div>
                    <span>{companyInfo.phone2}</span>
                  </a>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 text-sm text-gray-700 hover:text-purple-700 p-2 hover:bg-purple-50 rounded-lg transition-all"
                  >
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Mail className="w-4 h-4 text-purple-600" />
                    </div>
                    <span>{companyInfo.email}</span>
                  </a>
                  <a
                    href="https://g.co/kgs/JBGXRww"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-start gap-3 text-sm text-gray-700 hover:text-purple-700 p-2 hover:bg-purple-50 rounded-lg transition-all"
                  >
                    <div className="p-2 bg-purple-100 rounded-lg mt-0.5 shrink-0">
                      <MapPin className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="leading-relaxed">
                      {companyInfo.address}
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
